import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Slide,
  Paper
} from '@mui/material';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone ||
                       document.referrer.includes('android-app://');
    
    setIsInstalled(isInstalled);

    if (!isInstalled) {
      // Listen for the beforeinstallprompt event
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        
        // Show our custom install prompt after a short delay
        setTimeout(() => {
          setShowPrompt(true);
        }, 3000); // Show after 3 seconds
      };

      // Listen for app installed event
      const handleAppInstalled = () => {
        setIsInstalled(true);
        setShowPrompt(false);
        setDeferredPrompt(null);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if already installed or dismissed this session
  if (isInstalled || sessionStorage.getItem('pwa-install-dismissed')) {
    return null;
  }

  return (
    <>
      {/* Floating Install Button */}
      {deferredPrompt && !showPrompt && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1000,
            display: { xs: 'block', md: 'none' } // Only show on mobile
          }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: '50px',
              overflow: 'hidden',
              background: 'linear-gradient(45deg, #1976d2 30%, #21CBF3 90%)',
              animation: 'pulse 2s infinite'
            }}
          >
            <Button
              onClick={() => setShowPrompt(true)}
              startIcon={<InstallMobileIcon />}
              sx={{
                color: 'white',
                fontWeight: 'bold',
                px: 3,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Install App
            </Button>
          </Paper>
        </Box>
      )}

      {/* Install Dialog */}
      <Dialog
        open={showPrompt && Boolean(deferredPrompt)}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleDismiss}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            mx: 2,
            maxWidth: '400px'
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 3 }}>
          <IconButton
            onClick={handleDismiss}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500'
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ mb: 2 }}>
            <InstallMobileIcon 
              sx={{ 
                fontSize: 64, 
                color: 'primary.main',
                mb: 1
              }} 
            />
          </Box>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Install MySocialBiz Card Scanner
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get the full app experience! Install our business card scanner for:
          </Typography>

          <Box sx={{ textAlign: 'left', mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 🚀 Faster loading and better performance
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 📱 Works offline once installed
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 🎯 Direct access from your home screen
            </Typography>
            <Typography variant="body2">
              • 🔋 Less battery usage than browser
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleDismiss}
            color="inherit"
            sx={{ mr: 1 }}
          >
            Not Now
          </Button>
          <Button
            onClick={handleInstall}
            variant="contained"
            startIcon={<InstallMobileIcon />}
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #21CBF3 90%)',
              fontWeight: 'bold',
              px: 3
            }}
          >
            Install Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSS for pulse animation */}
      <style jsx global>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </>
  );
};

export default PWAInstallPrompt;